/**
 * Carousel Class
 * Handles touch/drag scrolling, prev/next buttons, and momentum scrolling.
 */
class Carousel {
  /**
   * @param {HTMLElement} wrapper - The carousel wrapper element.
   */
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.sliders = Array.from(wrapper.querySelectorAll(".js-carousel"));
    this.prevBtn = wrapper.querySelector(".carousel-prev");
    this.nextBtn = wrapper.querySelector(".carousel-next");
    this.isDragging = false;
    this.dragMoved = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.velocity = 0;
    this.momentumID = null;
    this.activeSlider = null;
    this.init();
  }

  /**
   * Initialize event listeners and carousel logic.
   */
  init() {
    if (!this.sliders.length) return;

    // Attach events to all sliders
    this.sliders.forEach((slider) => {
      // Drag events
      slider.addEventListener("mousedown", (e) => {
        this.activeSlider = slider;
        this.dragStart(e);
      });
      slider.addEventListener("mousemove", (e) => {
        this.activeSlider = slider;
        this.dragging(e);
      });
      slider.addEventListener(
        "touchstart",
        (e) => {
          this.activeSlider = slider;
          this.dragStart(e);
        },
        { passive: true }
      );
      slider.addEventListener(
        "touchmove",
        (e) => {
          this.activeSlider = slider;
          this.dragging(e);
        },
        { passive: false }
      );
      slider.addEventListener("touchend", () => this.dragStop());
      slider.addEventListener("scroll", () => this.updateButtons(slider));

      // Prevent accidental click-through on links
      slider.querySelectorAll("a").forEach((link) => {
        link.addEventListener("click", (e) => {
          if (this.dragMoved) e.preventDefault();
        });
      });

      // Prevent image ghost-drag and update buttons when images load
      slider.querySelectorAll("img").forEach((img) => {
        img.addEventListener("dragstart", (e) => e.preventDefault());
        if (!img.complete) {
          img.addEventListener("load", () => this.updateButtons(slider));
        }
      });
    });

    document.addEventListener("mouseup", () => this.dragStop());

    // Initialize prev/next buttons
    this.initButtons();

    // Run once after layout is ready
    requestAnimationFrame(() => this.updateButtons());
    window.addEventListener("load", () => this.updateButtons());
  }

  /**
   * Determine the current visible/active slider.
   * @returns {HTMLElement|null}
   */
  getCurrentSlider() {
    const visible = this.sliders.find(
      (s) => s.offsetParent !== null && s.clientWidth > 0
    );
    if (visible) return visible;
    if (this.activeSlider) return this.activeSlider;
    return this.sliders[0] || null;
  }

  /**
   * Handle drag start event.
   * @param {Event} e
   */
  dragStart(e) {
    const slider = this.getCurrentSlider();
    if (!slider) return;
    this.isDragging = true;
    this.dragMoved = false;
    slider.classList.add("dragging");
    this.startX = e.pageX ?? e.touches?.[0]?.pageX ?? 0;
    this.scrollLeft = slider.scrollLeft;
    this.velocity = 0;
    cancelAnimationFrame(this.momentumID);
  }

  /**
   * Handle dragging event.
   * @param {Event} e
   */
  dragging(e) {
    if (!this.isDragging) return;
    const slider = this.getCurrentSlider();
    if (!slider) return;

    const x = e.pageX ?? e.touches?.[0]?.pageX ?? 0;
    const dx = x - this.startX;
    if (Math.abs(dx) > 5) this.dragMoved = true;
    const prev = slider.scrollLeft;
    slider.scrollLeft = this.scrollLeft - dx;
    this.velocity = slider.scrollLeft - prev;
    this.updateButtons(slider);
  }

  /**
   * Handle drag stop event.
   */
  dragStop() {
    if (!this.isDragging) return;
    const slider = this.getCurrentSlider();
    this.isDragging = false;
    slider?.classList.remove("dragging");
    if (!this.dragMoved) return;
    this.momentum();
  }

  /**
   * Apply momentum scrolling after drag.
   */
  momentum() {
    const slider = this.getCurrentSlider();
    if (!slider) return;

    const momentum = () => {
      if (Math.abs(this.velocity) > 0.5) {
        slider.scrollLeft += this.velocity;
        this.velocity *= 0.95;
        this.updateButtons(slider);
        this.momentumID = requestAnimationFrame(momentum);
      }
    };
    momentum();
  }

  /**
   * Initialize prev/next button event listeners.
   */
  initButtons() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", () => {
        const slider = this.getCurrentSlider();
        if (!slider) return;
        slider.scrollBy({ left: -this.scrollStep(slider), behavior: "smooth" });
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", () => {
        const slider = this.getCurrentSlider();
        if (!slider) return;
        slider.scrollBy({ left: this.scrollStep(slider), behavior: "smooth" });
      });
    }
  }

  /**
   * Calculate scroll step (2 card widths).
   * @param {HTMLElement} slider
   * @returns {number}
   */
  scrollStep(slider) {
    const cardWidth = slider.querySelector(".product-card")?.offsetWidth || 200;
    return cardWidth * 2;
  }

  /**
   * Enable/disable prev/next buttons based on scroll position.
   * @param {HTMLElement} slider
   */
  updateButtons(slider = this.getCurrentSlider()) {
    if (!this.prevBtn || !this.nextBtn || !slider) return;
    const maxScroll = slider.scrollWidth - slider.clientWidth;
    if (slider.scrollLeft <= 0) {
      this.prevBtn.classList.add("is-disabled");
    } else {
      this.prevBtn.classList.remove("is-disabled");
    }
    if (slider.scrollLeft >= maxScroll - 1) {
      this.nextBtn.classList.add("is-disabled");
    } else {
      this.nextBtn.classList.remove("is-disabled");
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const wrappers = document.querySelectorAll(
    ".pdp-extras, .product-highlights, .related-products"
  );

  if (typeof Carousel !== "undefined" && wrappers.length > 0) {
    wrappers.forEach((wrapper) => {
      new Carousel(wrapper);
    });
  }
});
